import { useState, useRef } from "react";
import { createSecureLink } from "../api";
import { Lock, X, Paperclip, Send, Copy, Check, ExternalLink } from "lucide-react";

const EXPIRY_OPTIONS = [
  { label: "1 hour",   value: "1" },
  { label: "4 hours",  value: "4" },
  { label: "24 hours", value: "24" },
  { label: "3 days",   value: "72" },
  { label: "7 days",   value: "168" },
  { label: "30 days",  value: "720" },
];

const VIEW_OPTIONS = [
  { label: "Unlimited", value: "0" },
  { label: "1 view",    value: "1" },
  { label: "3 views",   value: "3" },
  { label: "5 views",   value: "5" },
  { label: "10 views",  value: "10" },
];

const S = {
  overlay:  { position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:20 },
  modal:    { background:"#131f35", border:"1px solid #1e3a5f", borderRadius:14, width:"100%", maxWidth:520, boxShadow:"0 24px 60px rgba(0,0,0,0.6)" },
  header:   { padding:"18px 20px", borderBottom:"1px solid #1e2d4a", display:"flex", alignItems:"center", gap:10 },
  body:     { padding:"20px" },
  label:    { display:"block", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:"#64748b", marginBottom:6 },
  input:    { width:"100%", background:"#0d1829", border:"1px solid #1e3a5f", borderRadius:8, padding:"9px 12px", color:"#f1f5f9", fontSize:13, outline:"none", boxSizing:"border-box" },
  select:   { width:"100%", background:"#0d1829", border:"1px solid #1e3a5f", borderRadius:8, padding:"9px 12px", color:"#f1f5f9", fontSize:13, outline:"none", cursor:"pointer" },
  textarea: { width:"100%", background:"#0d1829", border:"1px solid #1e3a5f", borderRadius:8, padding:"9px 12px", color:"#f1f5f9", fontSize:13, outline:"none", resize:"vertical", fontFamily:"inherit", boxSizing:"border-box" },
  grid2:    { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 },
  row:      { marginBottom:14 },
  btnPrimary: { display:"flex", alignItems:"center", gap:6, padding:"9px 18px", background:"linear-gradient(135deg,#1d4ed8,#3b82f6)", border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" },
  btnGhost:   { display:"flex", alignItems:"center", gap:6, padding:"9px 14px", background:"none", border:"1px solid #1e3a5f", borderRadius:8, color:"#64748b", fontSize:13, cursor:"pointer" },
  chip:     { display:"inline-flex", alignItems:"center", gap:5, padding:"3px 8px", background:"rgba(59,130,246,0.1)", border:"1px solid rgba(59,130,246,0.25)", borderRadius:6, fontSize:11, color:"#93c5fd" },
  err:      { background:"#450a0a", border:"1px solid #7f1d1d", borderRadius:6, padding:"8px 12px", fontSize:12, color:"#fca5a5", marginBottom:12 },
  ok:       { background:"#0d2a1a", border:"1px solid #166534", borderRadius:6, padding:"12px 16px", fontSize:12, color:"#86efac", marginBottom:12, display:"flex", flexDirection:"column", gap:8 },
};

export default function SecureLinkModal({ onClose, ticketId = null, defaultEmail = "", defaultTitle = "" }) {
  const fileRef  = useRef();
  const [form, setForm] = useState({
    title:           defaultTitle || "",
    recipient_email: defaultEmail || "",
    message:         "",
    expires_hours:   "24",
    max_views:       "0",
    anyone_with_link: false,
  });
  const [files,   setFiles]   = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [result,  setResult]  = useState(null);
  const [copied,  setCopied]  = useState(false);
  const [error,   setError]   = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFilePick = (e) => {
    setFiles(prev => [...prev, ...Array.from(e.target.files)]);
    e.target.value = "";
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, j) => j !== i));

  const handleSend = async () => {
    if (!form.recipient_email && !form.message && files.length === 0)
      return setError("Add a recipient, message, or at least one file.");
    setSaving(true); setError(null);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      if (ticketId) fd.append("ticket_id", ticketId);
      files.forEach(f => fd.append("files", f));
      const r = await createSecureLink(fd);
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to create link.");
    } finally { setSaving(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(result.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        {/* Header */}
        <div style={S.header}>
          <Lock size={16} color="#3b82f6" />
          <span style={{ fontSize:15, fontWeight:700, color:"#f1f5f9", flex:1 }}>Send Secure Data Link</span>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"#64748b", cursor:"pointer", display:"flex" }}><X size={16}/></button>
        </div>

        <div style={S.body}>
          {error && <div style={S.err}>{error}</div>}

          {result ? (
            <div>
              <div style={S.ok}>
                <span style={{ fontWeight:700, fontSize:13 }}>✅ Secure link created!</span>
                {result.url && (
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <code style={{ flex:1, fontSize:11, color:"#67e8f9", background:"#0a1a2e", padding:"7px 10px", borderRadius:6, wordBreak:"break-all" }}>{result.url}</code>
                    <button onClick={copyLink} style={{ ...S.btnPrimary, padding:"6px 12px", background: copied ? "#166534" : "linear-gradient(135deg,#1d4ed8,#3b82f6)", flexShrink:0 }}>
                      {copied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy</>}
                    </button>
                  </div>
                )}
                {form.recipient_email && (result.email && result.email.sent === false
                  ? <span style={{ fontSize:12, color:"#fbbf24" }}>⚠️ {result.email.error}</span>
                  : <span style={{ fontSize:12, color:"#86efac" }}>📧 Email sent to {form.recipient_email}</span>
                )}
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:8 }}>
                <a href={result.url} target="_blank" rel="noreferrer"
                  style={{ ...S.btnGhost, color:"#93c5fd", borderColor:"rgba(59,130,246,0.3)", textDecoration:"none" }}>
                  <ExternalLink size={13}/> Preview
                </a>
                <button onClick={onClose} style={S.btnPrimary}>Done</button>
              </div>
            </div>
          ) : (
            <>
              <div style={S.row}>
                <label style={S.label}>Title (optional — shown in the email, so no secrets here)</label>
                <input style={S.input} value={form.title} onChange={e => set("title", e.target.value)} placeholder="e.g. Password Reset, Configuration File..." />
              </div>

              <div style={S.row}>
                <label style={S.label}>Recipient Email</label>
                <input style={S.input} type="email" value={form.recipient_email} onChange={e => set("recipient_email", e.target.value)} placeholder="client@example.com — leave blank to skip email" />
              </div>

              {/* Identity lock — links addressed to a recipient are locked to them by default */}
              {form.recipient_email && (
                <div style={{ ...S.row, background: form.anyone_with_link ? "rgba(255,255,255,0.03)" : "rgba(29,78,216,0.1)", border: `1px solid ${form.anyone_with_link ? "#1e3a5f" : "rgba(59,130,246,0.3)"}`, borderRadius:8, padding:"12px 14px" }}>
                  <div style={{ fontSize:13, fontWeight:600, color: form.anyone_with_link ? "#94a3b8" : "#93c5fd", marginBottom:6, display:"flex", alignItems:"center", gap:6 }}>
                    <Lock size={13}/> {form.anyone_with_link ? "Open to anyone with the link" : `Locked to ${form.recipient_email}`}
                  </div>
                  <div style={{ fontSize:11, color:"#64748b", lineHeight:1.5, marginBottom:10 }}>
                    {form.anyone_with_link
                      ? "No identity check — anyone who has the URL can open it. Use only for non-sensitive content."
                      : "To open it, we email a one-time code to this address — no account needed (someone signed in to their portal opens it directly). Forwarded links won't open for anyone else."}
                  </div>
                  <label style={{ cursor:"pointer", display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#94a3b8" }}>
                    <input
                      type="checkbox"
                      checked={form.anyone_with_link}
                      onChange={e => set("anyone_with_link", e.target.checked)}
                      style={{ width:15, height:15, cursor:"pointer", accentColor:"#3b82f6" }}
                    />
                    Allow anyone with the link (skip identity check)
                  </label>
                </div>
              )}

              <div style={S.row}>
                <label style={S.label}>Message</label>
                <textarea style={S.textarea} rows={4} value={form.message} onChange={e => set("message", e.target.value)} placeholder="The secure content — passwords, notes… Never included in the email; shown only after the recipient verifies." />
              </div>

              {/* Files */}
              <div style={S.row}>
                <label style={S.label}>Attachments ({files.length})</label>
                {files.length > 0 && (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:8 }}>
                    {files.map((f, i) => (
                      <span key={i} style={S.chip}>
                        <Paperclip size={10}/>{f.name}
                        <button onClick={() => removeFile(i)} style={{ background:"none", border:"none", color:"#64748b", cursor:"pointer", padding:0, display:"flex", alignItems:"center" }}><X size={10}/></button>
                      </span>
                    ))}
                  </div>
                )}
                <input ref={fileRef} type="file" multiple style={{ display:"none" }} onChange={handleFilePick} />
                <button onClick={() => fileRef.current?.click()}
                  style={{ width:"100%", padding:"12px", background:"#0d1829", border:"2px dashed #1e3a5f", borderRadius:8, color:"#64748b", fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}
                  onMouseEnter={e => e.currentTarget.style.borderColor="#3b82f6"}
                  onMouseLeave={e => e.currentTarget.style.borderColor="#1e3a5f"}>
                  <Paperclip size={14}/> Click to add files (max 50MB each, 10 files)
                </button>
              </div>

              <div style={S.grid2}>
                <div>
                  <label style={S.label}>Link Expires</label>
                  <select style={S.select} value={form.expires_hours} onChange={e => set("expires_hours", e.target.value)}>
                    {EXPIRY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Access Limit</label>
                  <select style={S.select} value={form.max_views} onChange={e => set("max_views", e.target.value)}>
                    {VIEW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:20 }}>
                <button onClick={onClose} style={S.btnGhost}>Cancel</button>
                <button onClick={handleSend} disabled={saving} style={{ ...S.btnPrimary, opacity: saving ? 0.7 : 1 }}>
                  <Send size={13}/> {saving ? "Creating…" : form.recipient_email ? "Create & Send Email" : "Create Link"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
